// Calendar feed visual — parity port of insight-bi's insight-calendar-feed.
//
// insight-bi renders an Ignite UI `igx-calendar` month grid where every day that
// has data carries one coloured dot per event (cycling a palette), and selecting
// a day (or a weekday header) renders an `insight-feed` of that day's / week's
// records using the First/Second/Third Row Left/Right layout groups. This is a
// dependency-free React reimplementation with the same behaviour:
//   • the Date group supplies each event's date (bucketed by yyyy-MM-dd),
//   • days with events show up to 4 coloured indicator dots (palette from the
//     `calendarColorPallete` option) plus an optional text line (`showText`),
//   • clicking a day lists its events, clicking a weekday header lists that
//     week's events ("Upcoming Events"),
//   • the event list reuses <FeedsVisual> so the feed layout is byte-for-byte the
//     same as the standalone Feeds visual (groups, formatting, fallback).

import { useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import type { NativeRendererProps } from './nativeRenderers'
import { humanizeColumnLabel, mapVisualData, pickGroup, type MappedColumn, type MappedData } from './dataUtils'
import { FeedsVisual } from './FeedsVisual'
import type { VisualJsonData, VisualOption } from './types'

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] // weekStart = Monday (matches insight-bi)
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

const pad = (n: number) => String(n).padStart(2, '0')
const keyOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

/** Parse a value to a local-midnight Date (date-only strings parsed manually to
 *  avoid the UTC off-by-one), or null. */
function parseDate(v: unknown): Date | null {
  if (v == null || v === '') return null
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v
  const s = String(v)
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? null : d
}

// ── Colour palette (insight-bi's calendarColorPallete option) ────────────────
// Single names resolve to a 7-shade ramp; combos ("Green-SkyBlue-Pink") resolve
// to the lead shade of each named ramp, cycling per event. (insight-bi's source
// has a few malformed hex codes — corrected here to valid colours.)
const RAMPS: Record<string, string[]> = {
  pink: ['#FB9898', '#FBA2A2', '#FCADAD', '#FCB7B7', '#FDC1C1', '#FDCBCB', '#FDD6D6'],
  purple: ['#B29ECA', '#BAABCF', '#C1B1D5', '#C9BBDA', '#D1C5DF', '#D8CEE4', '#E0D8EA'],
  orange: ['#ED9768', '#EFA177', '#F1AC86', '#F2B695', '#F4C1A4', '#F6CBB3', '#F8D5C3'],
  skyblue: ['#22BBE9', '#38BFEB', '#4EC6ED', '#64CDF0', '#7AD4F2', '#90DBF4', '#A7E3F6'],
  green: ['#3EB390', '#51BA9B', '#65C2A6', '#7BCAB1', '#8BD1BC', '#9ED9C7', '#B2E1D2'],
  yellow: ['#F9AB03', '#FAB11C', '#FAB935', '#FBC24E', '#FBCB68', '#FCD381', '#FDDC9A'],
  darkblue: ['#0AB4FF', '#229DFF', '#3B9DFF', '#53A9FF', '#6CB5FF', '#84C1FF', '#9DCEFF'],
}
const DEFAULT_PALETTE = ['#3EB390', '#22BBE9', '#FB9898', '#F9AB03', '#B29ECA', '#ED9768', '#0AB4FF']

function resolvePalette(name?: string): string[] {
  const key = (name || '').trim()
  if (!key) return DEFAULT_PALETTE
  const tokens = key.split('-').map((t) => t.trim().toLowerCase()).filter(Boolean)
  if (tokens.length === 1) return RAMPS[tokens[0]] || DEFAULT_PALETTE
  const lead = tokens.map((t) => (RAMPS[t] || [])[0]).filter(Boolean)
  return lead.length ? lead : DEFAULT_PALETTE
}

/** Read an author-configured option value (the chosen value, else its default). */
function readOption(options: VisualOption[] | undefined, name: string): string | undefined {
  for (const o of options || []) {
    if (o && o.optionName === name) {
      const v = (o.value ?? o.defaultValue) as unknown
      return v == null ? undefined : String(v)
    }
  }
  return undefined
}

/** The date column: the Date group, else a DATE/datetime column, else the first
 *  column whose values parse as dates. */
function pickDateColumn(md: MappedData): MappedColumn | undefined {
  const g = pickGroup(md, 'date')
  if (g.length) return g[0]
  const cols: MappedColumn[] = Object.keys(md.schema)
    .filter((k) => k !== 'id')
    .map((k) => ({ name: k, label: humanizeColumnLabel(k), type: md.schema[k]?.type }))
  return (
    cols.find((c) => c.coltype === 'DATE' || c.type === 'datetime' || c.type === 'date') ||
    cols.find((c) => md.rows.some((r) => parseDate(r[c.name]) !== null))
  )
}

/** Columns shown in the per-day indicator text line (showText='true'): every
 *  visible feed column except the date itself. */
function textColumns(md: MappedData, dateName: string): MappedColumn[] {
  const groups = ['firstrowleft', 'firstrowright', 'secondrowleft', 'secondrowright', 'thirdrowleft', 'thirdrowright']
  const out: MappedColumn[] = []
  const seen = new Set<string>()
  for (const gname of groups) {
    for (const c of pickGroup(md, gname)) {
      if (c.hidden || c.name === dateName || seen.has(c.name)) continue
      seen.add(c.name)
      out.push(c)
    }
  }
  return out
}

export function CalendarVisual({ jsonData, visualLayout, optionsArr, onSelect }: NativeRendererProps) {
  const md = useMemo(() => mapVisualData(jsonData, visualLayout), [jsonData, visualLayout])
  const dateCol = useMemo(() => pickDateColumn(md), [md])

  const options = optionsArr ?? visualLayout?.options
  const palette = useMemo(() => resolvePalette(readOption(options, 'calendarColorPallete')), [options])
  const showText = useMemo(() => (readOption(options, 'showText') || 'false') === 'true', [options])

  // Bucket rows by their day key (yyyy-MM-dd).
  const byDay = useMemo(() => {
    const map = new Map<string, Array<Record<string, unknown>>>()
    if (!dateCol) return map
    for (const r of md.rows) {
      const d = parseDate(r[dateCol.name])
      if (!d) continue
      const k = keyOf(d)
      if (!map.has(k)) map.set(k, [])
      map.get(k)!.push(r)
    }
    return map
  }, [md, dateCol])

  // Default month + selection: today if it has events, else the latest event day.
  const initial = useMemo(() => {
    const keys = [...byDay.keys()].sort()
    const todayKey = keyOf(new Date())
    const sel = byDay.has(todayKey) ? todayKey : keys[keys.length - 1] || todayKey
    const d = parseDate(sel) || new Date()
    return { year: d.getFullYear(), month: d.getMonth(), selected: sel }
  }, [byDay])

  const [view, setView] = useState({ year: initial.year, month: initial.month })
  // Selection is either a single day (mode 'day') or a weekday-of-month
  // ("Upcoming Events", mode 'week'); insight-bi supports both.
  const [sel, setSel] = useState<{ mode: 'day'; day: string } | { mode: 'week'; weekday: number }>(
    { mode: 'day', day: initial.selected },
  )
  // Re-sync month + selection when the data (and thus the initial day) changes
  // — the standard "adjust state when inputs change during render" pattern.
  const [lastInit, setLastInit] = useState(initial.selected)
  const feedRef = useRef<HTMLDivElement>(null)
  if (lastInit !== initial.selected) {
    setLastInit(initial.selected)
    setView({ year: initial.year, month: initial.month })
    setSel({ mode: 'day', day: initial.selected })
  }

  if (!dateCol) return <Notice text="No date column to build a calendar (map a Date column group)." />

  // Build the 6×7 grid starting on the Monday on/before the 1st.
  const first = new Date(view.year, view.month, 1)
  const startOffset = (first.getDay() + 6) % 7 // 0=Mon … 6=Sun
  const gridStart = new Date(view.year, view.month, 1 - startOffset)
  const cells: Date[] = Array.from({ length: 42 }, (_, i) =>
    new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i))
  const todayKey = keyOf(new Date())

  // Selecting a day/weekday changes the feed below the grid, which in a
  // fixed-height host is usually scrolled out of view — so the click looked
  // like it did nothing. Bring the feed into view.
  const select = (next: typeof sel) => {
    setSel(next)
    requestAnimationFrame(() => feedRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }))
  }

  const shiftMonth = (delta: number) => {
    const m = view.month + delta
    setView({ year: view.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 })
  }

  // Resolve the currently-selected rows + a heading for the feed panel.
  let feedRows: Array<Record<string, unknown>> = []
  let feedHeading = ''
  let feedEmpty = ''
  if (sel.mode === 'day') {
    feedRows = byDay.get(sel.day) || []
    const d = parseDate(sel.day)
    feedHeading = d ? d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : sel.day
    feedEmpty = 'No data available for selected date.'
  } else {
    // All days of the currently-viewed grid that fall on the chosen weekday.
    const dayKeys = cells.filter((d) => (d.getDay() + 6) % 7 === sel.weekday).map(keyOf)
    feedRows = dayKeys.flatMap((k) => byDay.get(k) || [])
    feedHeading = `Upcoming Events — ${WEEKDAYS[sel.weekday]}`
    feedEmpty = 'No data available for selected week.'
  }

  // Reuse FeedsVisual for the event list: a [schemaRow, ...selectedRows] slice
  // renders identically to the standalone Feeds visual (groups + formatting).
  const feedData: VisualJsonData | null = feedRows.length
    ? ([jsonData[0], ...feedRows] as VisualJsonData)
    : null
  const txtCols = showText ? textColumns(md, dateCol.name) : []

  return (
    // minHeight, not height: in a fixed-height host (designer preview, dashboard
    // tile) a height of 100% let the month grid take the whole box and squeezed
    // the feed below to nothing, so clicking a day "showed nothing". The
    // calendar now grows past the box and the host scrolls, and the feed keeps
    // a floor — insight-bi likewise draws the feed BELOW the calendar.
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%', padding: 10, gap: 10, boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="button" onClick={() => shiftMonth(-1)} style={navBtn} aria-label="Previous month">‹</button>
        <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--pg-heading, #111)', minWidth: 150, textAlign: 'center' }}>
          {MONTHS[view.month]} {view.year}
        </div>
        <button type="button" onClick={() => shiftMonth(1)} style={navBtn} aria-label="Next month">›</button>
        <button
          type="button"
          onClick={() => { const t = new Date(); setView({ year: t.getFullYear(), month: t.getMonth() }); select({ mode: 'day', day: keyOf(t) }) }}
          style={{ ...navBtn, width: 'auto', padding: '2px 10px', marginLeft: 4 }}
        >
          Today
        </button>
      </div>

      {/* Month grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {WEEKDAYS.map((w, wi) => {
          const weekend = wi >= 5
          const active = sel.mode === 'week' && sel.weekday === wi
          return (
            <button
              key={w}
              type="button"
              onClick={() => select({ mode: 'week', weekday: wi })}
              title={`Show ${w} events for this month`}
              style={{
                textAlign: 'center', fontSize: 11, fontWeight: 600, padding: '2px 0',
                border: 'none', cursor: 'pointer', borderRadius: 4, background: active ? 'var(--pg-primary, #3b82f6)' : 'transparent',
                color: active ? '#fff' : weekend ? 'var(--pg-danger, #e11d48)' : 'var(--pg-muted, #6b7280)',
              }}
            >
              {w}
            </button>
          )
        })}
        {cells.map((d, i) => {
          const k = keyOf(d)
          const inMonth = d.getMonth() === view.month
          const dayRows = byDay.get(k) || []
          const count = dayRows.length
          const isSel = sel.mode === 'day' && k === sel.day
          const isToday = k === todayKey
          return (
            <button
              key={i}
              type="button"
              onClick={() => select({ mode: 'day', day: k })}
              title={count ? `${count} event${count === 1 ? '' : 's'}` : undefined}
              style={{
                position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                minHeight: showText ? 46 : 36, borderRadius: 6, cursor: 'pointer', fontSize: 13,
                border: isToday ? '1px solid var(--pg-primary, #3b82f6)' : '1px solid transparent',
                boxShadow: isSel ? '0 0 0 2px var(--pg-primary, #3b82f6)' : undefined,
                background: count ? 'var(--pg-primary-soft, rgba(59,130,246,0.10))' : 'transparent',
                color: inMonth ? 'var(--pg-text, #222)' : 'var(--pg-muted, #9ca3af)',
                fontWeight: count ? 600 : 400, padding: '3px 0 4px',
              }}
            >
              <span>{d.getDate()}</span>
              {count > 0 && (
                showText
                  ? <DayText rows={dayRows} cols={txtCols} palette={palette} />
                  : <Dots count={count} palette={palette} />
              )}
            </button>
          )
        })}
      </div>

      {/* Events for the selected day / week — rendered via the shared Feeds visual */}
      <div ref={feedRef} style={{ flex: '1 0 auto', minHeight: 180, display: 'flex', flexDirection: 'column', borderTop: '1px solid var(--pg-border, #e5e7eb)', paddingTop: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--pg-heading, #111)', marginBottom: 6 }}>
          {feedHeading}
          <span style={{ marginLeft: 8, fontWeight: 400, color: 'var(--pg-muted, #6b7280)' }}>
            {feedRows.length} event{feedRows.length === 1 ? '' : 's'}
          </span>
        </div>
        <div style={{ flex: '1 0 auto' }}>
          {feedData
            ? <FeedsVisual jsonData={feedData} visualLayout={visualLayout} optionsArr={optionsArr} onSelect={onSelect} />
            : <div style={{ fontSize: 12, color: 'var(--pg-muted, #6b7280)', padding: '8px 0' }}>{feedEmpty}</div>}
        </div>
      </div>
    </div>
  )
}

// Up to four coloured indicator dots (one per event, palette cycling); a
// trailing "+N" when a day has more events than dots.
function Dots({ count, palette }: { count: number; palette: string[] }) {
  const shown = Math.min(count, 4)
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, lineHeight: 1 }}>
      {Array.from({ length: shown }, (_, i) => (
        <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: palette[i % palette.length] }} />
      ))}
      {count > 4 && <span style={{ fontSize: 8, fontWeight: 700, color: 'var(--pg-muted, #6b7280)', marginLeft: 1 }}>+{count - 4}</span>}
    </span>
  )
}

// showText mode: a coloured dot + a short text line per event (up to 4), built
// from the non-date feed columns — insight-bi's showAllEventIndicatorWithText.
function DayText({ rows, cols, palette }: { rows: Array<Record<string, unknown>>; cols: MappedColumn[]; palette: string[] }) {
  const shown = rows.slice(0, 4)
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 1, width: '100%', overflow: 'hidden' }}>
      {shown.map((r, i) => {
        const text = cols.map((c) => r[c.name]).filter((v) => v != null && v !== '').map(String).join(' ')
        return (
          <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, lineHeight: '11px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: palette[i % palette.length] }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>
          </span>
        )
      })}
      {rows.length > 4 && <span style={{ fontSize: 8, fontWeight: 700, color: 'var(--pg-muted, #6b7280)' }}>+{rows.length - 4} more</span>}
    </span>
  )
}

function Notice({ text }: { text: string }) {
  return <div style={{ padding: 16, fontSize: 13, color: 'var(--pg-muted, #888)', textAlign: 'center' }}>{text}</div>
}

const navBtn: CSSProperties = {
  width: 28, height: 28, borderRadius: 6, border: '1px solid var(--pg-border, #e5e7eb)',
  background: 'var(--pg-surface, #fff)', color: 'var(--pg-text, #222)', cursor: 'pointer', fontSize: 16, lineHeight: 1,
}

export default CalendarVisual
